import { Spin } from 'antd'
import { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/DocumentTableColumns'
import { MiddleTruncatedText } from 'features/knowledgeDocumentViewer/components/MiddleTruncatedText'
import { useDocumentThumbnail } from 'features/knowledgeDocumentViewer/hooks/useDocumentThumbnail'
import { toTitleCase } from 'features/knowledgeDocumentViewer/utils/textHelpers'
import React from 'react'
import styled from 'styled-components'

const CARD_WIDTH = 120
const THUMBNAIL_HEIGHT = 100

const Card = styled.button<{ $isSelected?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${CARD_WIDTH}px;
  flex-shrink: 0;
  padding: 0.25rem;
  background: ${(props) =>
    props.$isSelected
      ? props.theme.forecast.activeButton
      : props.theme.forecast.defaultButton};
  border-radius: 0.25rem;
  overflow: hidden;
  cursor: pointer;
  text-align: left;
  border: 2px solid transparent;

  &:hover {
    background-color: ${(props) =>
      props.$isSelected
        ? props.theme.forecast.hoverGhostButton
        : props.theme.forecast.hoverButton};
  }
  &:active {
    background-color: ${(props) => props.theme.forecast.activeButton};
    color: ${(props) => props.theme.forecast.activeText};
  }
`

const ThumbnailContainer = styled.div`
  width: 100%;
  height: ${THUMBNAIL_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: ${(props) => props.theme.forecast.cardGroupBackground};
`

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.375rem 0.25rem 0 0.25rem;
  gap: 0.125rem;
`

const DrawingNumber = styled.div`
  font-size: 0.625rem;
  color: ${(props) => props.theme.forecast.secondaryText};
`

const Title = styled.div`
  font-size: 0.6875rem;
  font-weight: 500;
  color: ${(props) => props.theme.forecast.defaultText};
`

interface RecentDocumentCardProps {
  document: DocumentTableRow
  isSelected?: boolean
  onClick: (document: DocumentTableRow, event?: React.MouseEvent) => void
  onContextMenu?: (document: DocumentTableRow, event: React.MouseEvent) => void
}

export const RecentDocumentCard: React.FC<RecentDocumentCardProps> = ({
  document,
  isSelected = false,
  onClick,
  onContextMenu,
}) => {
  const { thumbnailUrl, isLoading } = useDocumentThumbnail({
    document,
    scale: 1,
  })

  const handleClick = (event: React.MouseEvent) => {
    onClick(document, event)
  }

  const handleContextMenu = (event: React.MouseEvent) => {
    if (onContextMenu) {
      onContextMenu(document, event)
    }
  }

  const rawDisplayTitle = document.documentTitle || document.title
  const displayTitle = toTitleCase(rawDisplayTitle)
  const displayDrawingNumber =
    document.drawing ||
    (rawDisplayTitle !== document.title ? document.title : null)

  return (
    <Card
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      $isSelected={isSelected}>
      <ThumbnailContainer>
        {isLoading ? (
          <Spin size='small' />
        ) : thumbnailUrl ? (
          <ThumbnailImage src={thumbnailUrl} alt={displayTitle} />
        ) : null}
      </ThumbnailContainer>
      <CardContent>
        {displayDrawingNumber && (
          <DrawingNumber>
            <MiddleTruncatedText
              text={displayDrawingNumber}
              maxWidth='100%'
              fillWidth
              tooltipFontSize='0.625rem'
            />
          </DrawingNumber>
        )}
        <Title>
          <MiddleTruncatedText
            text={displayTitle}
            maxWidth='100%'
            fillWidth
            tooltipFontSize='0.6875rem'
          />
        </Title>
      </CardContent>
    </Card>
  )
}
